// src/redux/groups/userGroupesPosts.js
import { createAsyncThunk } from '@reduxjs/toolkit';
import GroupPostAPI from './groupPostAPI';

export const getGroupPosts = createAsyncThunk(
  'groupPosts/getGroupPosts',
  async ({ groupId, skip = 0, limit = 10 }, { rejectWithValue }) => {
    try {
      const data = await GroupPostAPI.getGroupPosts({ groupId, skip, limit });
      return { groupId, ...data, skip, limit }; // { posts, has_more, groupId, skip, limit }
    } catch (err) {
      return rejectWithValue(err.message || 'Failed to fetch posts');
    }
  }
);

export const createGroupPost = createAsyncThunk(
  'groupPosts/createGroupPost',
  async ({ groupId, title, content, evidence = [], disclosesPosition = false }, { rejectWithValue }) => {
    try {
      const post = await GroupPostAPI.createGroupPost({ groupId, title, content, evidence, disclosesPosition });
      return post;
    } catch (err) {
      // Backend wraps error details as {error: {message}}, not {detail} --
      // reading err.message alone lost the real reason (e.g. the
      // verify-your-email gate's message) and fell back to a generic one.
      return rejectWithValue(err.response?.data?.error?.message || err.message || 'Failed to create post');
    }
  }
);

export const updateGroupPost = createAsyncThunk(
  'groupPosts/updateGroupPost',
  async ({ postId, payload }, { rejectWithValue }) => {
    try {
      const post = await GroupPostAPI.updateGroupPost(postId, payload);
      return post;
    } catch (err) {
      return rejectWithValue(err.message || 'Failed to update post');
    }
  }
);

export const deleteGroupPost = createAsyncThunk(
  'groupPosts/deleteGroupPost',
  async ({ postId }, { rejectWithValue }) => {
    try {
      const id = await GroupPostAPI.deleteGroupPost(postId);
      return { postId: id };
    } catch (err) {
      return rejectWithValue(err.message || 'Failed to delete post');
    }
  }
);

// ── Reactions ────────────────────────────────────────────────────────────────

export const likeGroupPost = createAsyncThunk(
  'groupPosts/likePost',
  async ({ postId }, { rejectWithValue }) => {
    try {
      const data = await GroupPostAPI.likePost(postId);
      // Normalise: backend returns post_id, we expose postId for consistency
      return { postId: data.post_id ?? postId, likes: data.likes, is_liked: data.is_liked };
    } catch (err) {
      return rejectWithValue(err.message || 'Failed to like post');
    }
  }
);

export const unlikeGroupPost = createAsyncThunk(
  'groupPosts/unlikePost',
  async ({ postId }, { rejectWithValue }) => {
    try {
      const data = await GroupPostAPI.unlikePost(postId);
      return { postId: data.post_id ?? postId, likes: data.likes, is_liked: data.is_liked };
    } catch (err) {
      return rejectWithValue(err.message || 'Failed to unlike post');
    }
  }
);

export const bullGroupPost = createAsyncThunk(
  'groupPosts/bullPost',
  async ({ postId }, { rejectWithValue }) => {
    try {
      const data = await GroupPostAPI.bullPost(postId);
      return {
        postId: data.post_id ?? postId,
        i_bulled: data.i_bulled,
        i_beared: data.i_beared,
        bull_count: data.bull_count,
        bear_count: data.bear_count,
      };
    } catch (err) {
      return rejectWithValue(err.message || 'Failed to bull post');
    }
  }
);

export const unbullGroupPost = createAsyncThunk(
  'groupPosts/unbullPost',
  async ({ postId }, { rejectWithValue }) => {
    try {
      const data = await GroupPostAPI.unbullPost(postId);
      return {
        postId: data.post_id ?? postId,
        i_bulled: data.i_bulled ?? false,
        bull_count: data.bull_count,
        bear_count: data.bear_count,
      };
    } catch (err) {
      return rejectWithValue(err.message || 'Failed to unbull post');
    }
  }
);

export const bearGroupPost = createAsyncThunk(
  'groupPosts/bearPost',
  async ({ postId }, { rejectWithValue }) => {
    try {
      const data = await GroupPostAPI.bearPost(postId);
      return {
        postId: data.post_id ?? postId,
        i_beared: data.i_beared,
        i_bulled: data.i_bulled ?? false,
        bull_count: data.bull_count,
        bear_count: data.bear_count,
      };
    } catch (err) {
      return rejectWithValue(err.message || 'Failed to bear post');
    }
  }
);

export const unbearGroupPost = createAsyncThunk(
  'groupPosts/unbearPost',
  async ({ postId }, { rejectWithValue }) => {
    try {
      const data = await GroupPostAPI.unbearPost(postId);
      return {
        postId: data.post_id ?? postId,
        i_beared: data.i_beared ?? false,
        bull_count: data.bull_count,
        bear_count: data.bear_count,
      };
    } catch (err) {
      return rejectWithValue(err.message || 'Failed to unbear post');
    }
  }
);

// ── Comments ─────────────────────────────────────────────────────────────────

export const createGroupComment = createAsyncThunk(
  'groupPosts/createComment',
  async ({ postId, content, parentCommentId = null }, { rejectWithValue }) => {
    try {
      const comment = await GroupPostAPI.createComment({ postId, content, parentCommentId });
      return { postId, comment };
    } catch (err) {
      return rejectWithValue(err.message || 'Failed to create comment');
    }
  }
);

export const updateGroupComment = createAsyncThunk(
  'groupPosts/updateComment',
  async ({ postId, commentId, content }, { rejectWithValue }) => {
    try {
      const comment = await GroupPostAPI.updateComment(commentId, content);
      return { postId, comment };
    } catch (err) {
      return rejectWithValue(err.message || 'Failed to update comment');
    }
  }
);

export const deleteGroupComment = createAsyncThunk(
  'groupPosts/deleteComment',
  async ({ postId, commentId }, { rejectWithValue }) => {
    try {
      await GroupPostAPI.deleteComment(commentId);
      return { postId, commentId };
    } catch (err) {
      return rejectWithValue(err.message || 'Failed to delete comment');
    }
  }
);

export const likeGroupComment = createAsyncThunk(
  'groupPosts/likeComment',
  async ({ postId, commentId }, { rejectWithValue }) => {
    try {
      await GroupPostAPI.likeComment(commentId);
      return { postId, commentId };
    } catch (err) {
      return rejectWithValue(err.message || 'Failed to like comment');
    }
  }
);

export const unlikeGroupComment = createAsyncThunk(
  'groupPosts/unlikeComment',
  async ({ postId, commentId }, { rejectWithValue }) => {
    try {
      await GroupPostAPI.unlikeComment(commentId);
      return { postId, commentId };
    } catch (err) {
      return rejectWithValue(err.message || 'Failed to unlike comment');
    }
  }
);